/**
 * The app shell (04 §0, HANDOFF §0.2, mockup AppShell): sidebar (desktop ≥ lg), top bar (search ⌘K, deadline
 * pill, notification bell, plan badge), the workspace-state banner, the test-workspace stripe, the mobile tab bar.
 * Everything permission-dependent comes from `GET /me` (entitlements, REQUESTS #36); the routes' own rules are
 * checked in `RouteGate`.
 *
 * The kit's navigation renders plain links; the shell turns same-origin clicks into router navigations, so a
 * click never reloads the app (tokens live in memory).
 *
 * The AI assistant (F12, screen 36): the top bar's button and the floating launcher; the panel loads on first use.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useMatches, useNavigate, useRouterState } from "@tanstack/react-router";
import type { UseQueryResult } from "@tanstack/react-query";
import { useGetMe } from "@brevalt/api-client/customer";
import type { Me } from "@brevalt/api-client/customer/model";
import { isApiError } from "@brevalt/api-client";
import { MobileTabBar, Sidebar, TopBar, type NavItem } from "@brevalt/ui";
import {
  allows,
  computeEntitlements,
  interceptLinks,
  isTestWorkspace,
  LoadingBlock,
  NoPermissionState,
  NoWorkspaceState,
  resolveWorkspace,
  WorkspaceDeletingState,
  prefs,
  ServerErrorState,
  SessionEndedDialog,
  useAuth,
  useErrorToast,
  useStageLabels,
  useSwitchWorkspace,
  viewerZone,
  workspace,
  type Entitlements,
} from "@brevalt/app-core";
import { config } from "../config";
import { activeNav, homeFor, MORE, NAV, navVisible, TAB_IDS, type NavDef } from "./nav";
import { useOpenCases } from "./open-cases";
import { DeadlinePill } from "./deadline-pill";
import { currentMembership, needsWorkspace, ShellPlanBadge, TestStripe, WorkspacePicker } from "./workspace";
import { WorkspaceBanner } from "./workspace-banner";
// Eager: its ⌘K shortcut must work from the first paint (a deferred listener missed an early key press).
import { GlobalSearch, MobileSearch } from "./search";
import {
  AccountActions,
  ActionResultNotice,
  AccountMenu,
  AssistantLauncher,
  AssistantTopBarButton,
  CreateWorkspace,
  NotificationBell,
  SecondFactorOffer,
  ShellWorkspaceSwitcher,
} from "./lazy";
import { DemoBadge } from "./demo-badge";

function navItems(
  defs: readonly NavDef[],
  e: Entitlements,
  t: ReturnType<typeof useTranslation>["t"],
  badges: Record<string, string | undefined>,
): NavItem[] {
  return defs
    .filter((n) => navVisible(n, e))
    .map((n) => ({
      id: n.id,
      label: n.label(t),
      icon: n.icon,
      href: n.to,
      // The kit types the badge as a number but renders any text: "100+" (REQUESTS #50 g).
      badge: badges[n.id] as unknown as number | undefined,
    }));
}

/** The main area: `/me` states, the route's permission, then the page. */
export function RouteGate({ me, entitlements }: { me: UseQueryResult<Me>; entitlements: Entitlements }) {
  const { t } = useTranslation();
  const session = useAuth();
  const toastError = useErrorToast();
  const switchTo = useSwitchWorkspace();
  const matches = useMatches();
  const error = me.error;
  // A stored workspace the member no longer belongs to (403/404 for it): back to the default workspace.
  const lostWorkspace = isApiError(error) && (error.status === 403 || error.status === 404) && workspace.get() !== null;
  // `/me` answered for the previous workspace (a switch just happened; its refetch is under way): wait for it.
  const stale = me.data !== undefined && !me.isError && me.dataUpdatedAt < workspace.changedAt();
  // A workspace chosen automatically in this tab and reset afterwards is never chosen again (no loop).
  const resolution = me.data && !stale ? resolveWorkspace(me.data, workspace.get(), workspace.autoSelected()) : null;
  const reset = lostWorkspace || resolution?.kind === "reset";
  const select = resolution?.kind === "select" ? resolution.tenantId : null;
  useEffect(() => {
    if (reset) switchTo(null);
    else if (select) {
      workspace.noteAutoSelected(select);
      switchTo(select);
    }
  }, [reset, select, switchTo]);

  if (me.isPending || stale || reset || select) return <LoadingBlock label={t("misc.one_moment")} />;
  if (me.isError) {
    if (isApiError(error) && error.status === 401) {
      return (
        <SessionEndedDialog
          open
          onSignIn={() => {
            session.signIn(window.location.pathname).catch(toastError);
          }}
        />
      );
    }
    return (
      <ServerErrorState
        requestId={isApiError(error) ? error.requestId : undefined}
        onRetry={() => me.refetch()}
        statusPageUrl={config.statusPageUrl}
      />
    );
  }
  if (resolution?.kind === "pick") return <WorkspacePicker options={resolution.options} />;
  // A new account (no workspace, sign-up's last step) names its company; one that lost its workspaces is told.
  if (!entitlements.tenant && needsWorkspace(me.data)) return <CreateWorkspace me={me.data} />;
  if (!entitlements.tenant) return <NoWorkspaceState />;
  // The deletion is in progress: nobody has access any more (HANDOFF §0.16); only this page.
  if (entitlements.tenantStatus === "deleting") {
    return (
      <>
        <WorkspaceDeletingState />
        {/* Phones have no sidebar: the switcher (other workspaces) and the account are here. */}
        <div className="mx-auto max-w-lg space-y-4 px-4 pb-8 lg:hidden">
          <ShellWorkspaceSwitcher me={me.data} />
          <AccountActions />
        </div>
      </>
    );
  }
  const guarded = [...matches].reverse().find((m) => m.staticData.permission !== undefined);
  if (guarded && !allows(entitlements.tenant, guarded.staticData.permission)) {
    return (
      <NoPermissionState
        page={guarded.staticData.title?.(t) ?? ""}
        role={entitlements.tenant.roleName}
        homeHref={homeFor(entitlements)}
      />
    );
  }
  return <Outlet />;
}

function ShellFrame({ me, children }: { me: Me | undefined; children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const entitlements = computeEntitlements(me);
  const zone = viewerZone(me);
  const test = isTestWorkspace(currentMembership(me));
  const [collapsed, setCollapsed] = useState(() => prefs.get("sidebarCollapsed") === "1");
  // A workspace being deleted has no access any more: only its full-page state, the switcher and the account.
  const usable = !!entitlements.tenant && entitlements.tenantStatus !== "deleting";
  // `GET /search` needs product.read (cases among the hits only with case.read).
  const searchable = usable && entitlements.can("product.read");
  // The test workspace has no real deadline pill (mockup 34): its drill cases show their own clocks.
  const cases = useOpenCases(!test && usable);
  const stageLabel = useStageLabels(!!cases.next);
  const active = activeNav(pathname);
  const badges = {
    cases: cases.count > 0 ? String(cases.count) : undefined,
  };
  const items = usable ? navItems(NAV, entitlements, t, badges) : [];
  const tabs = [
    ...navItems(
      TAB_IDS.flatMap((id) => NAV.filter((n) => n.id === id)),
      entitlements,
      t,
      badges,
    ),
    { id: MORE.id, label: MORE.label(t), icon: MORE.icon, href: MORE.to },
  ];
  // The stage's name in the page's language (`useStageLabels`); an unknown stage: a neutral label.
  const label = cases.next ? (stageLabel(cases.next.stage) ?? t("misc.deadline")) : undefined;
  const membership = currentMembership(me);
  return (
    // Clicks on same-origin links (also from the kit's portals: React events bubble through portals) navigate
    // inside the app. Keyboard activation of a link fires a click too.
    <div
      className="min-h-screen bg-bg lg:flex"
      onClick={interceptLinks((path) => {
        void navigate({ to: path });
      })}
    >
      <a
        href="#main"
        className="sr-only z-(--z-toast) rounded-btn bg-surface px-3 py-2 text-sm shadow-overlay focus:not-sr-only focus:fixed focus:left-2 focus:top-2"
      >
        {t("shell.skip_to_content")}
      </a>
      <Sidebar
        items={items}
        active={active}
        collapsed={collapsed}
        onCollapsedChange={(v) => {
          setCollapsed(v);
          prefs.set("sidebarCollapsed", v ? "1" : "0");
        }}
        footer={
          <>
            {me && <ShellWorkspaceSwitcher me={me} />}
            <AccountMenu name={me?.user.fullName} role={membership?.roleName} />
          </>
        }
      />
      <div className="min-w-0 flex-1 pb-launcher-clearance lg:pb-0">
        {test && <TestStripe />}
        {usable && <WorkspaceBanner entitlements={entitlements} zone={zone} />}
        {/* The authenticator-app offer (#169): its own chunk; on the demo too (every person has their own account). */}
        {usable && <SecondFactorOffer />}
        {/* Keycloak's answer to an action started in the app (#169), said once wherever the user returns. */}
        {usable && <ActionResultNotice />}
        <TopBar
          mobileBrand
          search={searchable ? <GlobalSearch /> : undefined}
          deadline={
            cases.next &&
            label && (
              <>
                <span className="lg:hidden">
                  <DeadlinePill deadline={cases.next} label={label} zone={zone} compact />
                </span>
                <span className="max-lg:hidden">
                  <DeadlinePill deadline={cases.next} label={label} zone={zone} />
                </span>
              </>
            )
          }
          actions={
            usable && (
              <>
                {searchable && <MobileSearch />}
                <AssistantTopBarButton />
                <NotificationBell />
                {entitlements.demo ? (
                  // The demo (#153 a): one calm badge on every screen size, never a plan or trial badge.
                  <span className="ml-1 flex items-center">
                    <DemoBadge />
                  </span>
                ) : (
                  <span className="ml-1 max-lg:hidden">
                    <ShellPlanBadge entitlements={entitlements} />
                  </span>
                )}
              </>
            )
          }
        />
        <main id="main" tabIndex={-1} className="outline-none">
          {children}
        </main>
      </div>
      {usable && <MobileTabBar items={tabs} active={active} />}
      {usable && <AssistantLauncher />}
    </div>
  );
}

/** `/me`, the workspace and the route's permission, then the page (for layouts without the shell frame). */
export function MeGate() {
  const me = useGetMe();
  return <RouteGate me={me} entitlements={computeEntitlements(me.data)} />;
}

/** The signed-in layout route. */
export function Shell() {
  const me = useGetMe();
  const entitlements = computeEntitlements(me.data);
  return (
    <ShellFrame me={me.data}>
      <RouteGate me={me} entitlements={entitlements} />
    </ShellFrame>
  );
}
